import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import SEO from '../components/SEO';

const COURSE_FORM_URL = 'https://forms.gle/VvwLJJH4gY94Wwd5A';

const Courses = () => {
  const { t } = useTranslation();
  const [coursesData, setCoursesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(coursesRef, orderBy('featured', 'desc'));
      const querySnapshot = await getDocs(q);
      const courses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCoursesData(courses);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCoursesData([]);
      setLoading(false);
    }
  };

  const inclusions = [
    t('courses.inclusions.accommodation'),
    t('courses.inclusions.meals'),
    t('courses.inclusions.sightseeing'),
    t('courses.inclusions.tuition'),
    t('courses.inclusions.certification'),
    t('courses.inclusions.mentorship'),
    t('courses.inclusions.souvenir'),
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Goldsmith and Silversmith Courses",
    "description": "Comprehensive goldsmithing and silversmithing courses from beginner to expert level. Learn handmade jewellery making with lifetime mentorship.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Kamal Institute",
      "url": "https://kamalinstitute.co.in"
    },
    "courseCode": "KI-GS",
    "educationalCredentialAwarded": "Certificate",
    "hasCourseInstance": [
      {
        "@type": "CourseInstance",
        "courseMode": "on-site",
        "duration": "P1W",
        "name": "Beginners Certificate"
      },
      {
        "@type": "CourseInstance",
        "courseMode": "on-site",
        "duration": "P2W",
        "name": "Intermediate Certificate"
      },
      {
        "@type": "CourseInstance",
        "courseMode": "on-site",
        "duration": "P1M",
        "name": "Expert Certificate"
      }
    ]
  };

  return (
    <div className="bg-white pb-16">
      <SEO
        title="Goldsmith & Silversmith Courses - Certificate Programs"
        description="Choose from Beginners (1 week), Intermediate (2 weeks), or Expert (1 month) certificate programs. All courses include accommodation, meals, sightseeing, and lifetime mentorship."
        keywords="goldsmith course, silversmith course, jewellery making course, beginners jewellery course, intermediate jewellery course, expert jewellery course, jewellery certificate, handmade jewellery training"
        url="/courses"
        structuredData={structuredData}
      />
      {/* Header */}
      <div className="bg-gradient-to-b from-gold-100/20 py-16 pt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {t('courses.title')}
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t('courses.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-16">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('courses.loading')}</p>
          </div>
        ) : coursesData.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">{t('courses.noCourses')}</p>
            <Link
              to="/admin"
              className="text-gold-600 hover:text-gold-500 font-semibold"
            >
              {t('courses.addInAdmin')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {coursesData.map((course) => (
            <div
              key={course.id}
              className={`relative flex flex-col rounded-3xl bg-white p-8 shadow-2xl ring-1 ${
                course.featured
                  ? 'ring-2 ring-gold-600 scale-105'
                  : 'ring-gray-900/10'
              }`}
            >
              {course.featured && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center rounded-full bg-gold-600 px-4 py-1 text-sm font-semibold text-white">
                    {t('courses.mostPopular')}
                  </span>
                </div>
              )}
              
              <div className="flex-1">
                <h3 className="text-2xl font-bold tracking-tight text-gray-900">
                  {course.name}
                </h3>
                <p className="mt-2 text-sm text-gray-600">{course.duration}</p>
                
                <p className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-5xl font-bold tracking-tight text-gray-900">
                    {course.price}
                  </span>
                </p>
                
                <p className="mt-6 text-base leading-7 text-gray-600">
                  {course.description}
                </p>
                
                <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                  {course.content && course.content.length > 0 ? (
                  course.content.map((item, index) => (
                    <li key={index} className="flex gap-x-3">
                      <CheckCircleIcon className="h-6 w-5 flex-none text-gold-600" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))
                ) : (
                  <li className="text-gray-500">{t('courses.contentComingSoon')}</li>
                )}
                </ul>
              </div>
              
              <a
                href={COURSE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-all ${
                  course.featured
                    ? 'bg-gold-600 text-white hover:bg-gold-500 focus-visible:outline-gold-600'
                    : 'bg-white text-gold-600 ring-2 ring-inset ring-gold-600 hover:bg-gold-50'
                }`}
              >
                {t('courses.enrollNow')}
              </a>
            </div>
            ))}
          </div>
        )}
      </div>

      {/* Fee Inclusions */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
        <div className="bg-gray-50 rounded-3xl px-6 py-16 sm:px-16">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t('courses.whatsIncluded')}
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              {t('courses.includedDesc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {inclusions.map((item, index) => (
              <div key={index} className="flex gap-x-4 items-start">
                <CheckCircleIcon className="h-6 w-6 flex-none text-gold-600" aria-hidden="true" />
                <span className="text-base leading-7 text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* USP Section */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
        <div className="rounded-3xl bg-gradient-to-r from-gold-600 to-gold-500 px-6 py-16 text-center sm:px-16">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t('courses.commitment')}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gold-50">
            {t('courses.commitmentDesc')} <span className="font-bold">{t('courses.lifetimeMentorship')}</span> {t('courses.commitmentDesc2')}
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-gold-100">
            <span className="font-semibold">{t('courses.note')}</span> {t('courses.noteText')}
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24 text-center">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {t('courses.readyToBegin')}
        </h2>
        <p className="mt-6 text-lg leading-8 text-gray-600">
          {t('courses.readyDesc')}
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <a
            href={COURSE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-gold-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-gold-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600 transition-all"
          >
            {t('courses.courseEnquiry')}
          </a>
          <Link
            to="/about"
            className="text-base font-semibold leading-6 text-gray-900 hover:text-gold-600 transition-colors"
          >
            {t('courses.learnMoreAboutUs')} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Courses;
